import { singleton, container } from 'tsyringe';
import { EventEmitter, useEvent } from '../EventEmitter';
import { UserPreferenceService } from './UserPreferenceService';
import { DEFAULT_CURRENCY, type SupportedCurrency } from '../CurrencyConstants';
import { useDiMemo } from '../DI';
import { ICompanyContextToken } from './CompanyContext';

export { DEFAULT_CURRENCY, type SupportedCurrency } from '../CurrencyConstants';

/**
 * Service for managing currency preferences at both global and company-specific levels.
 * This extends the UserPreferenceService pattern to support company-scoped preferences.
 */
@singleton()
export class CurrencyPreferenceService {
    private globalPrefService?: UserPreferenceService;
    private companyPrefServices = new Map<number, Promise<undefined | UserPreferenceService>>();
    private currentCurrency: SupportedCurrency = DEFAULT_CURRENCY;
    private loadedCompanyId?: number;
    private readonly currencyChanged = new EventEmitter<SupportedCurrency>(DEFAULT_CURRENCY);

    public constructor() {}

    /**
     * Initialize the global currency preference service
     */
    public initGlobal(globalPrefSvc: UserPreferenceService) {
        this.globalPrefService = globalPrefSvc;
    }

    /**
     * Get the current active currency preference
     */
    public getCurrency(): SupportedCurrency {
        this.ensureCompanyLoaded();
        return this.currentCurrency;
    }

    /**
     * Set the currency preference (company-scoped if in company context, global otherwise)
     */
    public async setCurrency(currency: SupportedCurrency) {
        this.currentCurrency = currency;
        this.currencyChanged.emit(currency);

        const companyId = this.getCurrentCompany()?.Id;
        if (companyId) {
            const companyPrefSvc = await this.getCompanyPrefSvc();
            companyPrefSvc?.set('preferredCurrency', currency);
        }
    }

    /**
     * Listen for currency changes
     */
    public listen() {
        return this.currencyChanged;
    }

    private async ensureCompanyLoaded() {
        if (this.loadedCompanyId !== this.getCurrentCompany()?.Id) {
            const prefSvc = await this.getCompanyPrefSvc();
            if (prefSvc) {
                const currency = prefSvc?.get('preferredCurrency', DEFAULT_CURRENCY) ?? DEFAULT_CURRENCY;
                if (currency !== this.currentCurrency) {
                    this.currentCurrency = currency;
                    this.currencyChanged.emit(currency);
                }
                this.loadedCompanyId = this.getCurrentCompany()?.Id ?? 0;
            }
        }
    }

    private async getCompanyPrefSvc() {
        const companyId = this.getCurrentCompany()?.Id;
        if (companyId && !this.companyPrefServices.has(companyId)) {
            const scopedPrefs = this.globalPrefService?.scope(companyId);
            if (scopedPrefs) {
                this.companyPrefServices.set(companyId, scopedPrefs);
            }
        }
        return await this.companyPrefServices.get(companyId ?? 0);
    }

    private getCurrentCompany() {
        return container.resolve(ICompanyContextToken) as undefined | { Id?: number | null };
    }
}

export function useUserPreferredCurrency() {
    const currencyPrefSvc = useDiMemo(CurrencyPreferenceService);
    useEvent(currencyPrefSvc.listen());
    return currencyPrefSvc.getCurrency();
}
